import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { type DropCardInput } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg/index.tsx';
import { Dialog } from '@sorare/core/src/components/dialog';
import { sportProfile } from '@sorare/core/src/contexts/currentUser/queries';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useImagePrefetcher } from '@sorare/core/src/hooks/useImagePrefetcher.ts';
import { sortBy } from '@sorare/core/src/lib/arrays';
import { CARD_SIZE } from '@sorare/core/src/lib/cards';
import type { ColorFormat } from '@sorare/core/src/lib/colors';
import { useGetCardSetConfig } from '@sorare/core/src/set';
import { TinderCardSwipe } from '#src/components/commonCard/TinderCardSwipe/index.tsx';

import { useEvents } from '@sorare/core/src/lib/events/useEvents.ts';
import { ClosePackRevealDialog_PackDialogStep } from '@sorare/core/src/protos/events/platform/web/events.ts';
import { PackTear } from './PackTear/index.tsx';
import type {
  RevealPackCardsDialog_anyCardInterface,
  claimPackCardsOnCloseMutation,
  claimPackCardsOnCloseMutationVariables,
} from './__generated__/index.graphql.ts';

const Root = styled(Vertical)`
  width: 100%;
  position: relative;
  isolation: isolate;
  flex: 1;
  min-height: 100%;
  overflow: visible;
`;

const TinderCardSwipeWrapper = styled(Vertical)`
  width: 100%;
  flex: 1;
  &.hidden {
    opacity: 0;
    position: absolute;
    inset: 0;
  }
`;

const Background = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
`;

const CLAIM_PACK_CARDS_ON_CLOSE_MUTATION = gql`
  mutation claimPackCardsOnCloseMutation(
    $input: claimCardsFromPackInput!
    $sport: Sport!
  ) {
    claimCardsFromPack(input: $input) {
      currentUser {
        slug
        ...CurrentUseProvider_sportProfile
      }
      errors {
        message
        code
      }
    }
  }
  ${sportProfile}
` as TypedDocumentNode<
  claimPackCardsOnCloseMutation,
  claimPackCardsOnCloseMutationVariables
>;

type Props = {
  open: boolean;
  onClose?: () => void;
  cards: RevealPackCardsDialog_anyCardInterface[];
  onContinue: () => void;
  packId: string;
  packPictureUrl: string;
  isFirstPack?: boolean;
  initialPackAnimationDone?: boolean;
  primaryColor: ColorFormat;
  secondaryColor: ColorFormat;
  firstStepTitle?: ReactNode;
  showPlayerDetails?: boolean;
  cardSetSlug: string;
};

export const RevealPackCardsDialog = ({
  open,
  onClose,
  cards: propCards,
  onContinue,
  isFirstPack,
  packId,
  packPictureUrl,
  initialPackAnimationDone = false,
  firstStepTitle,
  showPlayerDetails,
  primaryColor,
  secondaryColor,
  cardSetSlug,
}: Props) => {
  const getCardSetConfig = useGetCardSetConfig();
  const setConfig = getCardSetConfig(cardSetSlug);
  const { background } = setConfig.visual.cardReveal;
  const { SetIcon } = setConfig.visual.brand;
  // Sort cards by drop rank so that the best cards are revealed last
  const cards = useMemo(
    () => sortBy(c => -1 * (c.dropRank ?? Infinity), [...propCards]),
    [propCards]
  );
  const track = useEvents();

  const [packAnimationDone, setPackAnimationDone] = useState(
    initialPackAnimationDone
  );
  const { sport } = useSportContext();
  const isLaptopAndAbove = useIsLaptop();

  const [displayRecap, setDisplayRecap] = useState(false);

  const [lastCardRevealed, setLastCardRevealed] = useState(false);
  const [claimPackCardsOnClose] = useMutation(
    CLAIM_PACK_CARDS_ON_CLOSE_MUTATION
  );
  const [collectionAnimationActive, setCollectionAnimationActive] =
    useState(false);

  const [droppedCards, setDroppedCards] = useState<DropCardInput[]>([]);
  const { showNotification } = useSnackNotificationContext();
  const packImageLoaded = useImagePrefetcher([
    proxyUrl(packPictureUrl, {
      cropWidth: CARD_SIZE,
    }),
  ]);

  const onNextCard = (index: number) => {
    const isLastCard = cards.length - 1 === index;
    if (isLastCard) {
      setLastCardRevealed(true);
    }
  };

  const handleClose = async () => {
    onClose?.();
    track('Close Pack Reveal Dialog', {
      step: collectionAnimationActive
        ? ClosePackRevealDialog_PackDialogStep.COLLECTION_ANIMATION
        : displayRecap
          ? ClosePackRevealDialog_PackDialogStep.RECAP
          : lastCardRevealed
            ? ClosePackRevealDialog_PackDialogStep.ALL_CARDS_REVEALED
            : ClosePackRevealDialog_PackDialogStep.BEFORE_ALL_CARDS_REVEALED,
    });
    if ((!lastCardRevealed || displayRecap) && !collectionAnimationActive) {
      if (packId) {
        await claimPackCardsOnClose({
          variables: {
            sport,
            input: {
              packId,
              chosenCardSlugs: cards
                .map(card => card.slug)
                .filter(slug =>
                  droppedCards.every(
                    droppedCard => droppedCard.cardSlug !== slug
                  )
                ),
            },
          },
        });
        if (!displayRecap) {
          showNotification('packOpeningInterrupted');
        }
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      hideHeader
      fullWidth
      maxWidth="xs"
      disableBackdropClick
      fullScreen={!isLaptopAndAbove}
    >
      {({ CloseButton }) => (
        <Root
          style={
            {
              '--primary-color': primaryColor,
              '--secondary-color': secondaryColor,
            } as CSSProperties
          }
        >
          <Background style={{ ...background }} />
          <TinderCardSwipeWrapper
            className={classNames({ hidden: !packAnimationDone })}
          >
            <TinderCardSwipe
              cards={cards}
              cardSetSlug={cardSetSlug}
              onContinue={onContinue}
              isFirstPack={isFirstPack}
              packId={packId}
              droppedCards={droppedCards}
              setDroppedCards={setDroppedCards}
              packAnimationDone={packAnimationDone}
              closeButton={
                !!handleClose &&
                !isFirstPack &&
                !!isLaptopAndAbove && <CloseButton onClose={handleClose} />
              }
              showPlayerDetails={showPlayerDetails}
              displayRecap={displayRecap}
              setDisplayRecap={setDisplayRecap}
              onCollectionAnimationActive={() => {
                setCollectionAnimationActive(true);
              }}
              onNextCard={onNextCard}
            />
          </TinderCardSwipeWrapper>
          {!packAnimationDone && (
            <PackTear
              onDone={() => {
                setPackAnimationDone(true);
              }}
              firstCardUrl={cards.at(0)?.pictureUrl || ''}
              loading={!cards.length || !packImageLoaded}
              packPictureUrl={packPictureUrl}
              closeButton={
                !!handleClose &&
                !isFirstPack &&
                !!isLaptopAndAbove && <CloseButton onClose={handleClose} />
              }
              title={
                firstStepTitle || (
                  <Vertical as="span" center gap={1} className="text-center">
                    {!!SetIcon && <SetIcon size={6} />}
                    <FormattedMessage
                      defaultMessage="Trace the line{br}to open your pack"
                      values={{
                        br: <br />,
                      }}
                    />
                  </Vertical>
                )
              }
            />
          )}
        </Root>
      )}
    </Dialog>
  );
};

RevealPackCardsDialog.fragments = {
  anyCardInterface: gql`
    fragment RevealPackCardsDialog_anyCardInterface on AnyCardInterface {
      slug
      dropRank
      ...TinderCardSwipe_anyCardInterface
    }
    ${TinderCardSwipe.fragments.anyCardInterface}
  ` as TypedDocumentNode<RevealPackCardsDialog_anyCardInterface>,
};
